import { cn } from '@/lib/utils';
import { IllustrativeLabel } from '../ui/IllustrativeLabel';
import { toAreaPath, toPath } from '../ui/mock-chart-curve';
import { LIGHT_TOKENS } from './light-tokens';
import { PAPER_INK } from '../ui/paper-tokens';

/**
 * 2026-07-17 R7 integration seam (design contract L3.1 / brief §2f,
 * "MUST-FIX"): the paper-surface stroke/area-fill pair, derived from
 * `PAPER_INK.primary.hex` (`#1C1712`) the same way `LIGHT_TOKENS.curve`
 * derives its own pair from `#15171C` — `strokeOpacity` (a strength value,
 * not a hue) is reused unchanged across both surfaces.
 */
const PAPER_CURVE = {
  stroke: PAPER_INK.primary.hex,
  strokeOpacity: LIGHT_TOKENS.curve.strokeOpacity,
  areaFill: 'rgba(28,23,18,0.035)',
} as const;

export type BigStatCurveProps = {
  /**
   * [MAJOR fix, r4-p3-review-correctness.md]: REQUIRED, not hardcoded. This
   * primitive has no `useLocale()` (RSC, per every other `ui-light/**`
   * primitive's plain-string convention — locale resolution happens in the
   * calling section component), so it cannot localize its own SVG
   * `aria-label` internally; the caller MUST pass the resolved EN/RO string.
   * A hardcoded Romanian label previously shipped here regardless of page
   * locale — an EN-locale screen-reader visitor heard Romanian for this
   * graphic's accessible name.
   */
  label: string;
  /**
   * R5-P3 [ADV-5]: optional REAL raw head-counts (e.g. `SocialProofSnapshot.
   * monthlyLeads`), one point per month, oldest→newest. This is the
   * snapshot→curve boundary — raw counts are normalized max→1 HERE (never
   * upstream), keeping `mock-chart-curve.ts`'s `toPath`/`toAreaPath` 0-1
   * contract single-sourced. A degenerate all-equal series (including an
   * all-zero one) normalizes to a flat line — the honest shape for "no
   * variation", never a crash. Absent (default) → the existing illustrative
   * `CURVE_SERIES` sweep, UNCHANGED (every existing consumer/test keeps its
   * exact current behavior).
   */
  series?: readonly number[] | null;
  /**
   * 2026-07-17 R7 integration seam: additive, default `'light'` (byte-
   * identical to every existing consumer). `surface="paper"` re-inks the
   * stroke/area-fill to `PAPER_CURVE` above (`LightBand`'s own doc comment
   * has the full cross-lane seam rationale) and swaps the illustrative-mock
   * disclosure chip's own surface accordingly; the curve geometry (`toPath`/
   * `toAreaPath`, `mock-chart-curve.ts`) is untouched either way.
   */
  surface?: 'light' | 'paper';
  className?: string;
};

/**
 * Normalized `[0,1]` series — a deliberately upward illustrative sweep
 * ([AMENDED per B2]), NOT a real metric (Law #8 truth barrier — carries its
 * own `IllustrativeLabel` below, same as every other filled mock). Fixed,
 * not randomized, per the `mock-chart-curve.ts` dossier's ban on
 * hand-jittered curve data.
 */
const CURVE_SERIES = [0.04, 0.05, 0.07, 0.11, 0.18, 0.28, 0.41, 0.58, 0.78, 1] as const;

/**
 * Raw counts → `[0,1]`, max→1 ([ADV-5]). `mapPoints` (mock-chart-curve.ts)
 * already clamps to `[0,1]` internally, so this only needs to produce the
 * ratio, not re-clamp. `max <= 0` (every count zero — cannot occur through
 * the real `monthlyLeads` floor gate, which requires every month >= 20, but
 * guarded here since this is a general-purpose boundary function) renders a
 * flat line at the bottom rather than dividing by zero.
 */
function normalizeSeriesMaxToOne(raw: readonly number[]): number[] {
  const max = Math.max(...raw);
  if (max <= 0) return raw.map(() => 0);
  return raw.map((value) => value / max);
}

/** [AMENDED per B2]: exact viewBox + vertical inset — path bbox measures
 * ≥80% of viewBox width (points are spaced across the FULL width by
 * `mapPoints`) and ≥60% of height (the `[0.04,1]` series range covers ~86%
 * of the padded inner height at this `padY`) — both comfortably clear the
 * B2 floor without any axis/gridline/tick, which stays banned. */
const VIEW_WIDTH = 1200;
const VIEW_HEIGHT = 460;
const PAD_Y = 24;

/**
 * BigStatCurve — the light-band editorial sweep (masterplan §2.3 +
 * [AMENDED per B2], attio `t1N69kp03G` grammar recast in ink). A large
 * decorative curve, NOT a chart: no axes/gridlines/ticks/numeric labels —
 * one 1.5px ink stroke + one soft ink area fill. Reuses `ui/mock-chart-
 * curve.ts`'s `toPath()`/`toAreaPath()` (0-dep, RSC-safe) instead of forking
 * a second curve builder — P2 `ui/**` stays frozen, composed not re-inlined
 * (Law #12 anti-fork directive).
 */
export function BigStatCurve({ label, series, surface = 'light', className }: BigStatCurveProps) {
  // [ADV-5] snapshot→curve boundary: real raw counts normalize max→1 HERE;
  // absent/null series falls back to the illustrative sweep, byte-identical
  // to every pre-R5-P3 consumer/test.
  const isReal = series != null && series.length > 0;
  const points = isReal ? normalizeSeriesMaxToOne(series) : CURVE_SERIES;
  const paper = surface === 'paper';
  const curve = paper ? PAPER_CURVE : LIGHT_TOKENS.curve;

  const linePath = toPath(points, VIEW_WIDTH, VIEW_HEIGHT, PAD_Y);
  const areaPath = toAreaPath(points, VIEW_WIDTH, VIEW_HEIGHT, PAD_Y);

  return (
    <div data-primitive="BigStatCurve" data-surface={surface} className={cn('relative', className)}>
      <svg
        viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
        className="h-auto w-full"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={label}
      >
        <path data-stroke-role="area" d={areaPath} fill={curve.areaFill} stroke="none" />
        <path
          data-stroke-role="line"
          d={linePath}
          fill="none"
          stroke={curve.stroke}
          strokeOpacity={curve.strokeOpacity}
          strokeWidth={1.5}
          strokeLinecap="round"
        />
      </svg>
      {/* [ADV-23] fail-closed honesty: the label survives ONLY while the
          curve is genuinely illustrative. Real data means no disclosure
          claim to make here — the caller (SocialProofSection) renders the
          neutral visible caption + sr summary instead. 2026-07-18 (slop-
          audit follow-up, live-drive finding): `IllustrativeLabel` gained a
          proper `surface="paper"` option (`PAPER_INK.muted` + `PAPER_RULE.
          hairline`, the warm R7 family) — upgraded from the earlier
          `surface="light"` compromise (the OLDER, cooler `ui-light` gray,
          "close enough" but a documented residue) now that the correct
          option exists. */}
      {!isReal && <IllustrativeLabel surface="paper" />}
    </div>
  );
}
